/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { ThemeEditor } from "./ThemeEditor";
import { ThemeCreate } from "./ThemeCreate";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: "themeEditor.createPage.open",
		label: "Create Theme resource",
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const basePath = args[0] as string;

			const res = await dialogSvc.open(ThemeCreate, "themeEditor.createTheme", { basePath });

			if (res.resourceId) {
				commandsSvc.execute("resource.open", res.resourceId);
			}

			return res.resourceId;
		}
	});

	commandsSvc.register({
		id: "themeEditor.edit.saveResource",
		label: "Save Theme resource",
		handler: async () => {
			const themeEditor = viewSvc.getCurrentView();

			if (!(themeEditor instanceof ThemeEditor)) {
				return;
			}

			const viewInfo = themeEditor.getViewInfo();
			const resourceInfo = themeEditor.getResourceInfo();

			if (!viewInfo.isModified || resourceInfo.isBeingSaved) {
				return;
			}

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: "Saving theme...",
				isClosable: false
			});

			try {
				await themeEditor.save();

				toastSvc.add({
					type: TOAST_TYPE.SUCCESS,
					message: "Theme has been saved",
					timeoutMs: 2500
				});
			} catch (err) {
				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: "Couldn't save theme.",
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}
		}
	});
}
